// Catálogo estático de división político-administrativa de Ecuador (Fase 2
// del roadmap de formularios MSP — ver PLAN.md, "001 — campos nuevos de
// filiación", residencia estructurada). Fuente primaria: INEC, "Clasificador
// Geográfico Estadístico 2024 — Esquema de Codificación de la División
// Político Administrativa del País" (actualizado al 31-dic-2023),
// https://aplicaciones2.ecuadorencifras.gob.ec/SIN/descargas/cge2024.xls —
// descargado y parseado programáticamente (no transcrito a mano) contra la
// hoja "DPA 2024" del archivo oficial.
//
// Decisiones de limpieza tomadas al parsear (documentadas para quien lo
// vuelva a regenerar desde el .xls original):
// - Se excluyen las jurisdicciones marcadas con "*" en el archivo — el
//   propio archivo las documenta como jurisdicciones que cambiaron de
//   estado (rural→urbana, o pasaron a ser cantón propio) y quedaron como
//   entrada histórica duplicada, no la clasificación vigente.
// - Dentro de cada cantón, cuando la "cabecera cantonal" (código 50) y una
//   parroquia urbana numerada comparten el mismo nombre (~33 cantones,
//   ej. "MACHALA"/"MACHALA", "MANTA"/"MANTA" — dos códigos INEC reales y
//   distintos pero el mismo nombre), se conserva una sola entrada — dos
//   opciones idénticas en el select de residencia confunden más de lo que
//   aportan.
// - Quito (Distrito Metropolitano) usa su propia terminología ("parroquias
//   metropolitanas centrales/suburbanas" en vez de "urbanas/rurales") y
//   repite sus parroquias reagrupadas por zona más abajo en el mismo
//   archivo — ambas variantes se parsearon igual, la repetición se
//   descarta por el mismo criterio de deduplicación por nombre.
//
// Resultado: 24 provincias, 220 cantones, 1277 parroquias.
import catalogoRaw from './data/catalogo-ubicacion-ecuador.json';

export interface ParroquiaEcuador {
  readonly codigo: string;
  readonly nombre: string;
}

export interface CantonEcuador {
  readonly codigo: string;
  readonly nombre: string;
  readonly parroquias: readonly ParroquiaEcuador[];
}

export interface ProvinciaEcuador {
  readonly codigo: string;
  readonly nombre: string;
  readonly cantones: readonly CantonEcuador[];
}

export const PROVINCIAS_ECUADOR: readonly ProvinciaEcuador[] = catalogoRaw as ProvinciaEcuador[];

export function provinciaPorCodigo(codigo: string | undefined): ProvinciaEcuador | undefined {
  return PROVINCIAS_ECUADOR.find((p) => p.codigo === codigo);
}

export function cantonesDeProvincia(codigoProvincia: string | undefined): readonly CantonEcuador[] {
  return provinciaPorCodigo(codigoProvincia)?.cantones ?? [];
}

export function cantonPorCodigo(codigoProvincia: string | undefined, codigoCanton: string | undefined): CantonEcuador | undefined {
  return cantonesDeProvincia(codigoProvincia).find((c) => c.codigo === codigoCanton);
}

export function parroquiasDeCanton(codigoProvincia: string | undefined, codigoCanton: string | undefined): readonly ParroquiaEcuador[] {
  return cantonPorCodigo(codigoProvincia, codigoCanton)?.parroquias ?? [];
}

/**
 * Lookups por nombre (no por código) — para resolver a qué código corresponde una
 * dirección ya guardada en `Patient.address` (que se persiste por nombre, no por
 * código INEC, para que siga siendo legible fuera de esta app — ver
 * DatosDemograficosTab.tsx). Comparación exacta: los nombres siempre se escriben
 * verbatim desde este mismo catálogo, nunca tipeados a mano, así que no hace falta
 * normalizar mayúsculas/tildes.
 */
export function provinciaPorNombre(nombre: string | undefined): ProvinciaEcuador | undefined {
  return PROVINCIAS_ECUADOR.find((p) => p.nombre === nombre);
}

export function cantonPorNombre(codigoProvincia: string | undefined, nombre: string | undefined): CantonEcuador | undefined {
  return cantonesDeProvincia(codigoProvincia).find((c) => c.nombre === nombre);
}

export function parroquiaPorNombre(codigoProvincia: string | undefined, codigoCanton: string | undefined, nombre: string | undefined): ParroquiaEcuador | undefined {
  return parroquiasDeCanton(codigoProvincia, codigoCanton).find((p) => p.nombre === nombre);
}
